import { cloneElement, isValidElement, ReactElement, ReactNode } from 'react';
import {
    ExclamationTriangleIcon,
    ExclamationCircleIcon,
    CheckCircleIcon,
    InformationCircleIcon,
} from '@heroicons/react/24/outline';
import { cn } from '../../utils';
import { VariantProp, AlignProp, Variant, Align } from './Modal';

type IconProps = {
    children?: ReactNode;
} & VariantProp &
    AlignProp;

export default function ModalIcon({
    variant = Variant.NORMAL,
    align = Align.CENTER,
    children,
}: IconProps) {
    const iconColor = {
        [Variant.SUCCESS]: 'alli-text-green-700',
        [Variant.CAUTION]: 'alli-text-orange-700',
        [Variant.WARNING]: 'alli-text-orange-700',
        [Variant.ERROR]: 'alli-text-red-400',
        [Variant.NORMAL]: 'alli-text-blue-500',
    };

    let newChild = null;
    if (children && isValidElement(children)) {
        newChild = cloneElement(children as ReactElement, {
            className: cn(
                children?.props?.className,
                `${iconColor[variant]} alli-h-6 alli-w-6`,
            ),
            'aria-hidden': true,
        });
    }
    return (
        <div
            data-testid="modal-icon"
            className={cn(
                'alli-mx-auto alli-flex alli-h-10 alli-w-10 alli-shrink-0 alli-items-center alli-justify-center alli-rounded-full',
                {
                    'alli-flex-shrink-0 sm:alli-mx-0 sm:alli-h-10 sm:alli-w-10':
                        align === Align.LEFT,
                    'alli-bg-red-100': variant === Variant.ERROR,
                    'alli-bg-green-200': variant === Variant.SUCCESS,
                    'alli-bg-orange-200':
                        variant === Variant.CAUTION ||
                        variant === Variant.WARNING,
                    'alli-bg-blue-100': variant === Variant.NORMAL,
                },
            )}>
            {!children ? (
                <StandardIcons variant={variant} />
            ) : newChild ? (
                newChild
            ) : (
                children
            )}
        </div>
    );
}

type StandardIconsProps = VariantProp;

function StandardIcons({ variant = Variant.NORMAL }: StandardIconsProps) {
    const icons: { [key in Variant]: JSX.Element } = {
        [Variant.SUCCESS]: (
            <CheckCircleIcon
                className="alli-h-6 alli-w-6 alli-text-green-700"
                aria-hidden="true"
            />
        ),
        [Variant.CAUTION]: (
            <ExclamationTriangleIcon
                className="alli-h-6 alli-w-6 alli-text-orange-700"
                aria-hidden="true"
            />
        ),
        [Variant.WARNING]: (
            <ExclamationTriangleIcon
                className="alli-h-6 alli-w-6 alli-text-orange-700"
                aria-hidden="true"
            />
        ),
        [Variant.ERROR]: (
            <ExclamationCircleIcon
                className="alli-h-6 alli-w-6 alli-text-red-400"
                aria-hidden="true"
            />
        ),
        [Variant.NORMAL]: (
            <InformationCircleIcon
                className="alli-h-6 alli-w-6 alli-text-blue-500"
                aria-hidden="true"
            />
        ),
    };
    return variant && Object.keys(icons).includes(variant)
        ? icons[variant]
        : null;
}
